// =============================================================================
// screens/StoryViewer.tsx — full-screen Instagram-grade story viewer (P3-10).
//
// Ported from apps/cryb-native/app/story/viewer.tsx off Expo; upgraded to CRYB
// premium spec:
//   • Segmented progress bars (one per story), the active bar animating 0→100%.
//   • TAP-LEFT / TAP-RIGHT navigation zones.
//   • HOLD-TO-PAUSE anywhere on the media — press-and-hold freezes the progress
//     + hides the chrome for a clean look; release resumes from where it paused.
//   • Swipe-DOWN to close.
//   • Shared media primitives over the full-bleed media: MediaTopBar (back +
//     author) atop the segmented progress bars, and MediaTransportBar (play/
//     pause mirrors hold-to-pause; quick reactions ride the action cluster),
//     both dark glass over top/bottom gradient scrims for legibility.
//   • TEXT stories (synthetic backdrop) get the ParticleBackground media preset
//     (dark, intensity 0.35, count 4); real image/video media has no backdrop.
//   • Responsive: media constrained to a centred 9:16 frame on tablet/desktop
//     (never a stretched full-window image); phone stays full-bleed.
//
// Data + backend interactions (grouped /stories, auto-advance, mark-viewed,
// optimistic reactions) are UNCHANGED.
//
// react-native-video is loaded lazily so the screen stays tsc-clean + crash-free
// before the lib is installed (P4). See followUps.
// =============================================================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Pressable,
  ActivityIndicator,
  Animated,
  PanResponder,
  useWindowDimensions,
} from 'react-native';
import CachedImage from '../components/ui/CachedImage';
import { CommonActions, useNavigation, useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import LinearGradient from 'react-native-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons, type IoniconName } from '../components/ui/Icon';
import MediaTopBar from '../components/ui/MediaTopBar';
import MediaTransportBar, {
  type MediaTransportAction,
} from '../components/ui/MediaTransportBar';
import ParticleBackground from '../components/ui/ParticleBackground';
import Asset3D from '../components/ui/Asset3D';
import type { RootStackParamList } from '../navigation/types';
import {
  colors,
  spacing,
  radius,
  fonts,
  typographyScale,
  shadowTokens,
  colorTokens,
  getLayout,
  layout as layoutTokens,
} from '../constants/theme';
import { api, toPublicMediaUrl } from '../services/api';
import { markGroupViewed } from '../lib/storyViewed';

const STORY_DURATION_MS = 5000;
const dark = colorTokens.dark;

type Nav = NativeStackNavigationProp<RootStackParamList, 'StoryViewer'>;
type Rt = RouteProp<RootStackParamList, 'StoryViewer'>;

let RNVideo: any = null;
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  RNVideo = require('react-native-video').default ?? require('react-native-video');
} catch {
  RNVideo = null;
}

// Wire shapes from GET /stories (apps/api/src/services/stories.service.ts).
interface Story {
  id: string;
  type?: 'TEXT' | 'IMAGE' | 'VIDEO' | string;
  text?: string | null;
  mediaUrl?: string | null;
  createdAt?: string;
  expiresAt?: string;
  hasViewed?: boolean;
}

interface StoryGroup {
  userId: string;
  username?: string;
  displayName?: string;
  avatar?: string | null;
  hasUnviewed?: boolean;
  stories?: Story[];
}

// Quick reactions — types from the server's addReactionSchema enum.
const REACTIONS: { type: string; icon: IoniconName }[] = [
  { type: 'LIKE', icon: 'heart' },
  { type: 'FIRE', icon: 'flame' },
  { type: 'LAUGH', icon: 'happy' },
];

function timeAgo(dateStr?: string | null): string {
  if (!dateStr) return '';
  const ts = new Date(dateStr).getTime();
  if (isNaN(ts)) return '';
  const mins = Math.floor((Date.now() - ts) / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

export default function StoryViewerScreen() {
  const navigation = useNavigation<Nav>();
  const route = useRoute<Rt>();
  const insets = useSafeAreaInsets();
  const { width, height } = useWindowDimensions();
  const layout = getLayout(width);
  const userId = route.params?.userId;

  const [group, setGroup] = useState<StoryGroup | null>(null);
  const [loading, setLoading] = useState(true);
  // Distinguish a fetch FAILURE (retryable) from a genuinely expired/removed
  // story — the old code showed "expired" for a plain network error too.
  const [error, setError] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [myReactions, setMyReactions] = useState<Record<string, string>>({});
  // Coarse mirror of the active story's progress fraction (0–1) for the
  // transport bar's scrub track. Throttled off the animated listener so it
  // never adds per-frame re-renders; the segmented bars above stay the
  // authoritative, natively-timed progress.
  const [scrubFrac, setScrubFrac] = useState(0);

  const stories: Story[] = Array.isArray(group?.stories)
    ? (group!.stories!.filter((s) => s && s.id) as Story[])
    : [];
  const current: Story | undefined = stories[index];

  // ── Responsive story frame (9:16, centred on tablet/desktop) ──────────────
  const isFramed = layout !== 'phone';
  const frameWidth = isFramed
    ? Math.min(width - spacing.xxxl * 2, ((height - spacing.xxxl * 2) * 9) / 16, 460)
    : width;
  const frameHeight = isFramed ? (frameWidth * 16) / 9 : height;

  // ── Close (idempotent — guarded so swipe + auto-advance can't double-pop) ──
  const closingRef = useRef(false);
  const handleClose = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    // Optimistically clear this group's "unviewed" ring on Home without a
    // refetch (the /stories/:id/view POSTs are fire-and-forget). See
    // lib/storyViewed — StoryRow subscribes and re-derives the ring instantly.
    markGroupViewed(userId);
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{ name: 'Tabs', state: { routes: [{ name: 'Home' }] } } as any],
        })
      );
    }
  }, [navigation, userId]);

  // ── Load the grouped stories and select the requested user's group ──
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    (async () => {
      try {
        const res = await api<StoryGroup[]>('/stories');
        if (!active) return;
        if (!res.success) { setError(true); setLoading(false); return; }
        const groups = Array.isArray(res.data) ? res.data : [];
        setGroup(groups.find((g) => g?.userId === userId) ?? null);
      } catch {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [userId, reloadTick]);

  // ── Progress animation ────────────────────────────────────────────────────
  // Driven by a numeric ref so HOLD-TO-PAUSE can freeze at the exact fraction
  // reached and resume for the remaining time. The animated Value feeds the bar
  // width interpolation; a listener tracks the live fraction for resume.
  const progress = useRef(new Animated.Value(0)).current;
  const progressFrac = useRef(0);
  const animRef = useRef<Animated.CompositeAnimation | null>(null);
  const lastScrubTs = useRef(0);

  useEffect(() => {
    const id = progress.addListener(({ value }) => {
      progressFrac.current = value;
      // Mirror to state at ~8fps for the transport scrub (cheap, non-blocking).
      const now = Date.now();
      if (now - lastScrubTs.current >= 120) {
        lastScrubTs.current = now;
        setScrubFrac(value);
      }
    });
    return () => progress.removeListener(id);
  }, [progress]);

  const runProgress = useCallback(
    (from: number) => {
      animRef.current?.stop();
      const remaining = Math.max(0, STORY_DURATION_MS * (1 - from));
      const anim = Animated.timing(progress, {
        toValue: 1,
        duration: remaining,
        useNativeDriver: false, // width interpolation needs the JS driver
      });
      animRef.current = anim;
      anim.start(({ finished }) => {
        if (finished) setIndex((i) => i + 1);
      });
    },
    [progress]
  );

  // Restart per story (fresh from 0).
  useEffect(() => {
    if (loading || !current) return;
    progress.setValue(0);
    progressFrac.current = 0;
    setScrubFrac(0);
    if (!paused) runProgress(0);
    return () => animRef.current?.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, index, current?.id]);

  // Pause / resume without restarting the story.
  useEffect(() => {
    if (loading || !current) return;
    if (paused) {
      animRef.current?.stop();
    } else {
      runProgress(progressFrac.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused]);

  // Walking past the last story closes the viewer.
  useEffect(() => {
    if (!loading && stories.length > 0 && index >= stories.length) handleClose();
  }, [loading, index, stories.length, handleClose]);

  // ── Mark each displayed story as viewed (fire-and-forget, de-duped) ──
  const viewedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const id = current?.id;
    if (!id || viewedRef.current.has(id)) return;
    viewedRef.current.add(id);
    api(`/stories/${id}/view`, { method: 'POST' }); // api() never throws
  }, [current?.id]);

  // ── Video source (react-native-video plays the remote URL directly) ──
  const videoUrl =
    current?.type === 'VIDEO' && current?.mediaUrl ? toPublicMediaUrl(current.mediaUrl) : null;

  // ── Swipe down to close ──
  const panResponder = useMemo(
    () =>
      PanResponder.create({
        onMoveShouldSetPanResponder: (_e, g) => g.dy > 16 && Math.abs(g.dy) > Math.abs(g.dx) * 1.5,
        onPanResponderRelease: (_e, g) => {
          if (g.dy > 80) handleClose();
        },
      }),
    [handleClose]
  );

  // ── Tap navigation ──
  const goPrev = () => {
    setPaused(false);
    setIndex((i) => Math.max(0, i - 1));
  };
  const goNext = () => {
    setPaused(false);
    setIndex((i) => i + 1);
  };

  // ── Reactions (optimistic, fire-and-forget) ──
  const handleReact = (type: string) => {
    const id = current?.id;
    if (!id) return;
    setMyReactions((m) => ({ ...m, [id]: type }));
    api(`/stories/${id}/react`, { method: 'POST', body: JSON.stringify({ type }) });
  };

  const name = group?.displayName || group?.username || 'User';

  // Quick reactions become the transport bar's trailing action cluster — the
  // active reaction lights up in the accent (preserves the optimistic react()).
  const reactionActions: MediaTransportAction[] = REACTIONS.map((r) => {
    const active = !!current?.id && myReactions[current.id] === r.type;
    return {
      key: r.type,
      icon: active ? r.icon : (`${r.icon}-outline` as IoniconName),
      active,
      onPress: () => handleReact(r.type),
      accessibilityLabel: `React with ${r.type.toLowerCase()}`,
    };
  });

  const topPad = insets.top + (isFramed ? spacing.md : spacing.sm);
  const bottomPad = Math.max(insets.bottom, spacing.md);

  return (
    <View style={styles.container} {...panResponder.panHandlers}>
      {loading ? (
        <View style={styles.centerWrap}>
          <ActivityIndicator size="large" color={colors.primaryLight} />
        </View>
      ) : error ? (
        <View style={styles.centerWrap}>
          <Asset3D name="error" size={110} style={{ marginBottom: spacing.md }} />
          <Text style={styles.unavailableTitle}>Couldn't load story</Text>
          <Text style={styles.unavailableText}>Check your connection and try again.</Text>
          <TouchableOpacity
            style={styles.unavailableBtn}
            onPress={() => setReloadTick((n) => n + 1)}
            accessibilityRole="button"
            accessibilityLabel="Retry loading story"
            activeOpacity={0.85}
          >
            <Text style={styles.unavailableBtnText}>Retry</Text>
          </TouchableOpacity>
        </View>
      ) : !group || stories.length === 0 ? (
        <View style={styles.centerWrap}>
          <Asset3D name="error" size={110} style={{ marginBottom: spacing.md }} />
          <Text style={styles.unavailableTitle}>Story unavailable</Text>
          <Text style={styles.unavailableText}>It may have expired or been removed.</Text>
          <TouchableOpacity
            style={styles.unavailableBtn}
            onPress={handleClose}
            accessibilityRole="button"
            accessibilityLabel="Close story viewer"
            activeOpacity={0.85}
          >
            <Text style={styles.unavailableBtnText}>Close</Text>
          </TouchableOpacity>
        </View>
      ) : !current ? (
        <View style={styles.centerWrap} />
      ) : (
        <View style={styles.stage}>
          {/* ── The story frame (full-bleed on phone, 9:16 card on larger) ── */}
          <View
            style={[
              styles.frame,
              isFramed
                ? { width: frameWidth, height: frameHeight, borderRadius: radius.xl, ...shadowTokens.sheet }
                : styles.fill,
            ]}
          >
            {/* ── Media layer ── */}
            {current.type === 'TEXT' ? (
              <LinearGradient
                colors={[colors.primary, colors.primaryLight]}
                start={{ x: 0.1, y: 0 }}
                end={{ x: 0.9, y: 1 }}
                style={StyleSheet.absoluteFill}
              >
                {/* Text stories show a synthetic backdrop → dress it with the
                    media-preset aurora (dark, low intensity, few orbs). Media/
                    video stories show real media, so no particle layer there. */}
                <ParticleBackground theme="dark" intensity={0.35} count={4} />
                <View style={styles.textStoryWrap}>
                  <Text style={styles.textStoryText}>{current.text || ''}</Text>
                </View>
              </LinearGradient>
            ) : current.type === 'VIDEO' ? (
              videoUrl && RNVideo ? (
                <RNVideo
                  source={{ uri: videoUrl }}
                  style={StyleSheet.absoluteFill}
                  resizeMode="cover"
                  repeat
                  controls={false}
                  paused={paused}
                  ignoreSilentSwitch="ignore"
                />
              ) : (
                <View style={styles.centerWrap}>
                  <Ionicons name="play-circle-outline" size={64} color={dark.textFaint} />
                  <Text style={styles.unavailableText}>Video can't be played</Text>
                </View>
              )
            ) : (
              <CachedImage
                source={{ uri: toPublicMediaUrl(current.mediaUrl || '') }}
                style={StyleSheet.absoluteFill}
                resizeMode="cover"
              />
            )}

            {/* Legibility scrims */}
            <LinearGradient
              colors={['rgba(14,11,26,0.6)', 'rgba(14,11,26,0)']}
              style={styles.scrimTop}
              pointerEvents="none"
            />
            <LinearGradient
              colors={['rgba(14,11,26,0)', 'rgba(14,11,26,0.7)']}
              style={styles.scrimBottom}
              pointerEvents="none"
            />

            {/* Caption overlay for media stories that also carry text */}
            {current.type !== 'TEXT' && !!current.text && !paused && (
              <View style={[styles.captionWrap, { bottom: bottomPad + 84 }]} pointerEvents="none">
                <Text style={styles.captionText} numberOfLines={4}>
                  {current.text}
                </Text>
              </View>
            )}

            {/* ── Tap + hold zones ──
                Left third = previous, right two-thirds = next; long-press on
                either pauses (chrome hides for a clean look) until release. */}
            <Pressable
              style={styles.tapZoneLeft}
              onPress={goPrev}
              onLongPress={() => setPaused(true)}
              onPressOut={() => setPaused(false)}
              delayLongPress={180}
              accessibilityRole="button"
              accessibilityLabel="Previous story, hold to pause"
            />
            <Pressable
              style={styles.tapZoneRight}
              onPress={goNext}
              onLongPress={() => setPaused(true)}
              onPressOut={() => setPaused(false)}
              delayLongPress={180}
              accessibilityRole="button"
              accessibilityLabel="Next story, hold to pause"
            />

            {/* ── Top chrome: segmented progress bars (authoritative, natively
                timed) + the shared MediaTopBar (back · author · time). ── */}
            {!paused && (
              <>
                <View
                  style={[styles.progressRow, { top: topPad }]}
                  pointerEvents="none"
                >
                  {stories.map((s, i) => (
                    <View key={s.id || String(i)} style={styles.progressTrack}>
                      <Animated.View
                        style={[
                          styles.progressFill,
                          {
                            width:
                              i < index
                                ? '100%'
                                : i === index
                                  ? progress.interpolate({
                                      inputRange: [0, 1],
                                      outputRange: ['0%', '100%'],
                                    })
                                  : '0%',
                          },
                        ]}
                      />
                    </View>
                  ))}
                </View>

                <MediaTopBar
                  title={name}
                  subtitle={`@${group.username || 'user'} · ${timeAgo(current.createdAt)}`}
                  onBack={handleClose}
                  style={{ top: topPad + 14 }}
                />
              </>
            )}

            {/* ── Bottom chrome: shared MediaTransportBar. Play/pause mirrors the
                hold-to-pause state; the scrub reflects the active story; quick
                reactions ride the trailing action cluster. ── */}
            {!paused && (
              <MediaTransportBar
                progress={scrubFrac}
                playing={!paused}
                onPlayPause={() => setPaused((p) => !p)}
                actions={reactionActions}
                accent={colors.primaryLight}
              />
            )}
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: dark.bg,
  },
  stage: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  fill: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  frame: {
    overflow: 'hidden',
    backgroundColor: dark.surface,
  },
  centerWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    padding: spacing.xl,
  },
  unavailableRing: {
    width: 80,
    height: 80,
    borderRadius: radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1,
    borderColor: dark.border,
    marginBottom: spacing.sm,
  },
  unavailableTitle: {
    ...typographyScale.h3,
    color: colors.white,
  },
  unavailableText: {
    ...typographyScale.body,
    color: dark.textMuted,
    textAlign: 'center',
  },
  unavailableBtn: {
    marginTop: spacing.md,
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.xxl,
    paddingVertical: spacing.sm + 2,
    borderRadius: radius.full,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
    ...shadowTokens.card,
  },
  unavailableBtnText: {
    ...typographyScale.body,
    color: colors.white,
    ...fonts.semibold,
  },

  scrimTop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 170,
  },
  scrimBottom: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 200,
  },

  textStoryWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xxl,
  },
  textStoryText: {
    fontSize: 28,
    lineHeight: 38,
    color: colors.white,
    textAlign: 'center',
    ...fonts.bold,
    textShadowColor: 'rgba(0,0,0,0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 8,
  },
  captionWrap: {
    position: 'absolute',
    left: spacing.lg,
    right: spacing.lg,
    backgroundColor: 'rgba(26,26,46,0.6)',
    borderWidth: 1,
    borderColor: dark.border,
    borderRadius: radius.md,
    padding: spacing.md,
  },
  captionText: {
    ...typographyScale.body,
    fontSize: 16,
    color: colors.white,
    textAlign: 'center',
    ...fonts.semibold,
  },

  tapZoneLeft: {
    position: 'absolute',
    top: 120,
    bottom: 110,
    left: 0,
    width: '38%',
  },
  tapZoneRight: {
    position: 'absolute',
    top: 120,
    bottom: 110,
    right: 0,
    width: '62%',
  },

  progressRow: {
    position: 'absolute',
    left: layoutTokens.gutter,
    right: layoutTokens.gutter,
    flexDirection: 'row',
    gap: spacing.xs,
  },
  progressTrack: {
    flex: 1,
    height: 3,
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.3)',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
    backgroundColor: colors.white,
  },
});
